import React from "react";
import { LegalLayout, LegalSection } from "../components/LegalLayout";
import { businessDetails } from "../data/content";

const ext = { target: "_blank", rel: "noopener noreferrer" };
const linkCls = "font-semibold text-brand-teal underline hover:text-brand-tealHover";

export const AccessibilityPage = () => (
  <LegalLayout
    title="Accessibility Statement"
    description="Our commitment to making the Freeman Jones website accessible to everyone, in line with WCAG 2.2 level AA."
    path="/accessibility"
    updated="September 2026"
    testid="accessibility-page"
  >
    <LegalSection id="commitment" title="Our commitment">
      <p>
        Freeman Jones is committed to making this website accessible to as many people as
        possible, regardless of technology or ability. We aim to conform to the Web Content
        Accessibility Guidelines (WCAG) 2.2 at level AA.
      </p>
    </LegalSection>

    <LegalSection id="what-we-have-done" title="What we've done">
      <p>To make this website easier to use for everyone, we have:</p>
      <ul className="list-disc space-y-2 pl-5">
        <li>Used clear, plain language and a logical heading structure</li>
        <li>Provided a "skip to content" link and semantic page landmarks</li>
        <li>
          Made the site fully usable with a keyboard, including focus management in menus and
          dialogs
        </li>
        <li>
          Used text and background colour combinations designed to meet WCAG AA contrast
          requirements
        </li>
        <li>Made buttons and links at least 44 pixels high, so they are easier to tap</li>
        <li>
          Designed the site to adapt to different screen sizes and to remain usable when text
          is enlarged
        </li>
        <li>
          Reduced animation and movement for people whose device is set to prefer reduced
          motion
        </li>
        <li>Added labels to interactive elements so they make sense when read aloud by screen readers</li>
      </ul>
    </LegalSection>

    <LegalSection id="known-limitations" title="Known limitations">
      <p>
        Some of our PDF documents — such as IVA protocol and standard conditions documents —
        may not be fully accessible to screen reader software. If you need any document in a
        different format, please contact us and we will do our best to help.
      </p>
      <p>
        Third-party services we link to — such as the Customer App, Digital Postbox, Trustpilot
        and MoneyHelper — are the responsibility of their respective providers.
      </p>
    </LegalSection>

    <LegalSection id="feedback" title="Feedback and contact">
      <p>
        If you find any accessibility problems on this website, or need information in a
        different format (such as large print, easy read or audio), please contact us:
      </p>
      <ul className="list-disc space-y-2 pl-5">
        <li>
          Call us on{" "}
          <a href={businessDetails.phoneLink} className={linkCls}>
            {businessDetails.phoneDisplay}
          </a>
        </li>
        <li>
          Email{" "}
          <a href={businessDetails.emailLink} className={linkCls}>
            {businessDetails.email}
          </a>
        </li>
      </ul>
      <p>We aim to respond to accessibility feedback within 5 working days.</p>
    </LegalSection>

    <LegalSection id="enforcement" title="If you're not happy with our response">
      <p>
        If you've contacted us about an accessibility issue and you're not happy with how we've
        responded, you can get independent advice from the{" "}
        <a {...ext} href="https://www.equalityadvisoryservice.com/" className={linkCls}>
          Equality Advisory and Support Service (EASS)
        </a>
        , which advises on rights under the Equality Act 2010, including access to services and
        websites.
      </p>
    </LegalSection>

    <LegalSection id="about-this-statement" title="About this statement">
      <p>
        This statement was prepared in September 2026. It is based on a self-assessment of this
        website against WCAG 2.2 level AA and will be reviewed whenever the site changes
        significantly.
      </p>
    </LegalSection>
  </LegalLayout>
);
